import {
    Button,
    Divider,
    Grid,
    TextField,
    Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Stack } from "@mui/material";
import DateField from "components/CustomFields/DateField";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import { useEffect, useState } from "react";
import { dbGet, dbPost } from "utils/DBFetchers";
import { getCurrentDate } from "utils/dateGetters";
  
  // styles
import useStyles from "./styles";
import { set } from "lodash";

const FILTERS = {
fromPeriod: null,
toPeriod: null,
program: null,
presentationIds: [],
};

const BASES_CLIENTES_DOCUMENTATION =
  "https://cfr-config.s3.sa-east-1.amazonaws.com/cfr_rules_documentation/definicion-panel-bases-pacientes.pdf";

const PROGRAMS = [
    { name: "Receta Solidaria Tradicional", programIds: [1] },
    { name: "Receta Solidaria Quemables", programIds: [1000] },
    { name: "Receta Solidaria Tradicionales + Quemables", programIds: [1, 1000] },
    { name: "Solo Mujer", programIds: [2] },
    { name: "Mi Salcobrand", programIds: [4] },
    { name: "Club + Cruz Verde", programIds: [6] },
  ];

export default function BasesClientesSelectionForm(props) {
    var classes = useStyles();
    const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
    const [filters, setFilters] = useState(FILTERS);
    const [presentations, setPresentations] = useState([]);
    const [lastImportId, setLastImportId] = useState(null);
    const [lastPayloadKey, setLastPayloadKey] = useState(null);
  
    useEffect(() => {
      getInitialInformation();
    }, []);

    const buildPayload = () => ({
        program_ids: filters.program ? filters.program.programIds : null,
        from_period: getPeriodFormatted(filters.fromPeriod),
        to_period: getPeriodFormatted(filters.toPeriod),
        presentation_ids: filters.presentationIds.length > 0 ? filters.presentationIds : null,
      });

    const getInitialInformation = () => {
        // Reutilizamos el endpoint existente que trae las presentaciones
        dbGet("kpi-control-panel/audit/filters")
          .then((data) => {
            setPresentations(data.presentations || []);
          })
          .catch((error) => {
            snackbarOpen("error", "Error al cargar las presentaciones");
          });
      };

      const getPeriodFormatted = (date) => {
        if (!date) return null;
        return parseInt(
          date.toISOString().split("T")[0].substring(0, 7).replace("-", "")
        );
      };
    
      const validateFilters = () => {
        if (!filters.program) {
            snackbarOpen("error", "Debe seleccionar un programa obligatoriamente.");
            return false;
        }
        if (
            filters.fromPeriod &&
            filters.toPeriod &&
            getPeriodFormatted(filters.fromPeriod) > getPeriodFormatted(filters.toPeriod)
        ) {
            snackbarOpen("error", "El período final no puede ser menor al período inicial.");
            setFilters((currentFilters) => ({ ...currentFilters, toPeriod: null }));
            return false;
        }
        return true;
    };

    const runApply = (downloadAfter = false) => {
        if (!validateFilters()) return;

        setLastImportId(null);
        setLastPayloadKey(null);
        props.setIsLoading(true);

        const payload = buildPayload();

        dbPost("bases-clientes/panel", payload)
            .then((response) => {
                setTimeout(updateProgress.bind(null, response.id, payload, downloadAfter), 3000);
            })
            .catch(() => {
                snackbarOpen("error", "Error al procesar la solicitud. Intente nuevamente.");
                props.setIsLoading(false);
            });
    };

    const handleApplyFilters = () => runApply(false);

    const updateProgress = (id, payload, downloadAfter) => {
        dbGet(`request_checker/status?id=${id}`)
            .then((response) => {
                if (response.status === "Completed") {
                    setLastImportId(id);
                    setLastPayloadKey(JSON.stringify(payload));
                    props.generateRowsData(response.data);
                    props.setIsLoading(false);

                    if (downloadAfter && props.exportFunction && response.data.excel_url) {
                      props.exportFunction(response.data.excel_url);
                    }
                } else if (!response.error_status && response.status !== "Failure") {
                    setTimeout(updateProgress.bind(null, id, payload, downloadAfter), 2000);
                } else {
                    snackbarOpen("error", "Error al calcular los pacientes. Intente nuevamente.");
                    props.setIsLoading(false);
                }
            })
            .catch(() => {
                snackbarOpen("error", "Error al verificar el estado de la solicitud. Intente nuevamente.");
                props.setIsLoading(false);
            });
    };

    const handleDownload = () => {
      if (!validateFilters()) return;

      const payload = buildPayload();
      const alreadyApplied = lastImportId && lastPayloadKey === JSON.stringify(payload);

      if (alreadyApplied) {
          // Aplicar ya corrió con estos filtros: descarga directa
          props.exportFunction(); 
      } else {
          // Nunca se aplicó (o cambiaron los filtros): aplica y descarga al terminar
          runApply(true);
      }
    };
        
        const handleProgramChange = (event, value) => {
            setFilters({ ...filters, program: value || null });
        };

        const handlePresentationChange = (event, value) => {
            const ids = value ? value.map((p) => p.id).flat() : [];
            setFilters({
            ...filters,
            presentationIds: ids,
            });
        };
        
        const handleFromPeriodChange = (date) => {
            if (
              date &&
              filters.toPeriod &&
              getPeriodFormatted(date) >
                getPeriodFormatted(filters.toPeriod)
            ) {
              snackbarOpen(
                "error",
                "El período final no puede ser menor al período inicial.",
              );
            }

            setFilters((currentFilters) => ({
              ...currentFilters,
              fromPeriod: date,
              toPeriod:
                date &&
                currentFilters.toPeriod &&
                getPeriodFormatted(date) >
                  getPeriodFormatted(currentFilters.toPeriod)
                  ? null
                  : currentFilters.toPeriod,
            }));
        };

        const handleToPeriodChange = (date) => {
            if (
              date &&
              filters.fromPeriod &&
              getPeriodFormatted(date) <
                getPeriodFormatted(filters.fromPeriod)
            ) {
              snackbarOpen(
                "error",
                "El período final no puede ser menor al período inicial.",
              );
              setFilters((currentFilters) => ({
                ...currentFilters,
                toPeriod: null,
              }));
              return;
            }

            setFilters((currentFilters) => ({
              ...currentFilters,
              toPeriod: date,
            }));
        };

        return (
            <>
              <CustomizedSnackbars
                severity={snackbarStatus.severity}
                mssg={snackbarStatus.message}
                open={snackbarStatus.open}
                onClose={onCloseSnackbar}
              />
              <Grid item xs={12} lg={12} style={{ marginBottom: "10px", marginLeft: "2.5%" }}>
                <Typography variant="h4" className={classes.alignedText}>
                  Selección
                </Typography>
                <Button
                  component="a"
                  href={BASES_CLIENTES_DOCUMENTATION}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  variant="text"
                >
                  Ver Documentación
                </Button>
                <Divider style={{ marginTop: 10 }} />
              </Grid>
        
              <Grid container justifyContent="center" spacing={2} style={{ marginTop: 10 }}>
                <Grid item container xs={12} justifyContent="center" spacing={2}>
                  <Grid item container xs={5} alignItems="center">
                    <Grid item xs={4} className={classes.alignRight}>
                      <Typography className={classes.alignedTextWithPadding} weight="bold">
                        Programa *
                      </Typography>
                    </Grid>
                    <Grid item xs={8} className={classes.alignLeft}>
                      <Autocomplete
                        className={classes.simpleSelectionField}
                        id="program"
                        autoHighlight
                        onChange={handleProgramChange}
                        options={PROGRAMS}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                          <TextField {...params} label="Seleccione" variant="standard" />
                        )}
                      />
                    </Grid>
                  </Grid>
        
                  <Grid item container xs={5} alignItems="center">
                    <Grid item xs={3} className={classes.alignRight}>
                      <Typography className={classes.alignedTextWithPadding} weight="bold">
                        Presentaciones
                      </Typography>
                    </Grid>
                    <Grid item xs={9} className={classes.alignLeft}>
                      <Autocomplete
                        multiple
                        className={classes.simpleSecondColumnSelectionField}
                        id="presentation"
                        autoHighlight
                        onChange={handlePresentationChange}
                        options={presentations}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                          <TextField {...params} label="Todas (Por defecto)" variant="standard" />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
        
                <Grid item container xs={12} justifyContent="center" spacing={2} style={{ marginTop: "5px" }}>
                  <Grid item container xs={5} alignItems="flex-start">
                    <Grid item xs={4} className={classes.alignRight}>
                      <Typography className={classes.alignedText} weight="bold">
                        Período inicial
                      </Typography>
                    </Grid>
                    <Grid item xs={8} className={classes.alignLeft} style={{ flexDirection: "column" }}>
                      <DateField
                        views={["year", "month"]}
                        maxDate={getCurrentDate()}
                        format={"MM/yyyy"}
                        value={filters.fromPeriod}
                        onChange={handleFromPeriodChange}
                        fieldClassName={classes.dateSelectionField}
                        clearable
                      />
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 30, marginTop: 5 }}>
                        - Vacío considera el histórico
                      </Typography>
                    </Grid>
                  </Grid>
        
                  <Grid item container xs={5} alignItems="flex-start">
                    <Grid item xs={3} className={classes.alignRight}>
                      <Typography className={classes.alignedText} weight="bold">
                        Período final
                      </Typography>
                    </Grid>
                    <Grid item xs={9} className={classes.alignLeft} style={{ flexDirection: "column" }}>
                      <DateField
                        views={["year", "month"]}
                        maxDate={getCurrentDate()}
                        format={"MM/yyyy"}
                        value={filters.toPeriod}
                        onChange={handleToPeriodChange}
                        fieldClassName={classes.dateSelectionField}
                        clearable
                      />
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 30, marginTop: 5 }}>
                        - Vacío considera hasta las últimas operaciones
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
        
              <Grid
                item
                xs={12}
                style={{ marginTop: "30px", marginBottom: "10px" }}
                className={classes.uploadButton}
              >
                <Stack direction="row" spacing={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApplyFilters}
                    disabled={props.isLoading}
                  >
                    Aplicar
                  </Button>
                  <Button
                    disabled={props.isLoading || !filters.program}
                    variant="contained"
                    color="secondary"
                    onClick={handleDownload}
                  >
                    Descargar base de pacientes contactables
                  </Button>
                </Stack>
              </Grid>
            </>
          );
    }
    

